import React from 'react';
import { AgFinancingPhase4Status, AgGroup } from '@indigo-ag/schema';

import i18next from 'core/i18n';
import { IStep, Stepper, StepStatus } from 'shared';

interface CessionGroupTimelineProps {
  cessionGroup: AgGroup;
}

const getStepsForCessionGroup = (cessionGroup: AgGroup): IStep[] => {
  const hasBeenAssignedToFund = !!cessionGroup.agFinancingProcesses[0]?.phase4?.sentToFundAt;
  const hasBeenReceivedByFund = !!cessionGroup.agFinancingProcesses[0]?.phase4?.receivedByFundAt;
  const hasBeenSentToCM = !!cessionGroup.agFinancingProcesses[0]?.phase4?.sentToManagerAt;
  const hasBeenSubmitted = !!cessionGroup.agFinancingProcesses[0]?.phase4?.submittedAt;

  return [
    {
      id: '1',
      title: i18next.t('fundAssignment'),
      completed: hasBeenAssignedToFund,
      status: hasBeenAssignedToFund ? StepStatus.COMPLETED : StepStatus.UNCOMPLETED
    },
    {
      id: '2',
      title: i18next.t('fundInformationReceive'),
      disabled: !hasBeenAssignedToFund,
      completed: hasBeenReceivedByFund,
      status: hasBeenReceivedByFund ? StepStatus.COMPLETED : StepStatus.UNCOMPLETED
    },
    {
      id: '3',
      title: i18next.t('fundInformationReview'),
      disabled: !hasBeenReceivedByFund,
      completed: hasBeenSentToCM,
      status: hasBeenSentToCM ? StepStatus.COMPLETED : StepStatus.UNCOMPLETED
    },
    {
      id: '4',
      title: i18next.t('awaitingApproval'),
      disabled: !hasBeenSentToCM,
      completed: hasBeenSubmitted,
      status: getApprovalStatus(cessionGroup.status)
    },
    {
      id: '5',
      title: i18next.t('submittedMinuta'),
      disabled: !hasBeenSubmitted,
      completed: hasBeenSubmitted,
      status: hasBeenSubmitted ? StepStatus.COMPLETED : StepStatus.UNCOMPLETED
    }
  ];
};

const getApprovalStatus = (status: AgGroup['status']): StepStatus => {
  switch (status) {
    case AgFinancingPhase4Status.SUBMITTED_MINUTA:
      return StepStatus.COMPLETED;
    case AgFinancingPhase4Status.DENIED:
      return StepStatus.DENIED;
    default:
      return StepStatus.UNCOMPLETED;
  }
};

const getActiveStepIdForCessionGroup = (cessionGroup: AgGroup): IStep['id'] => {
  switch (cessionGroup.status) {
    case AgFinancingPhase4Status.AWAITING_ASSIGNMENT:
      return '1';

    case AgFinancingPhase4Status.ASSIGNED_TO_FUND:
      return '2';

    case AgFinancingPhase4Status.RECEIVED_BY_FUND:
      return '3';

    case AgFinancingPhase4Status.AWAITING_MANAGER_APPROVAL:
      return '4';

    case AgFinancingPhase4Status.SUBMITTED_MINUTA:
      return '5';

    default:
      return '1';
  }
};

export const CessionGroupTimeline: React.FunctionComponent<CessionGroupTimelineProps> = ({ cessionGroup }) => {
  return (
    <Stepper
      steps={getStepsForCessionGroup(cessionGroup)}
      activeStepId={getActiveStepIdForCessionGroup(cessionGroup)}
    />
  );
};
